import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import { useContextMenu } from 'components/common/context-menu-manager';
import { ContextMenuItemType, LineSetPointType } from 'data/types';
import { useCallback } from 'react';

export type LineSetPointProps = {
  point: LineSetPointType;
  pointIndex: number;
  linesetUniqueId: string;
  canDeletePoint: boolean;
  onDeletePoint?: (pointUniqueId: string) => void;
  onDeleteLineset?: () => void;
  radius?: number;
  transparent?: boolean;
  previewPosition: { positionX: number; positionY: number };
  readOnly?: boolean;
  draggable?: boolean;
};

export default function LineSetPoint({
  point,
  pointIndex,
  linesetUniqueId,
  canDeletePoint,
  onDeletePoint,
  onDeleteLineset,
  radius,
  transparent = false,
  previewPosition,
  readOnly = false,
  draggable = true
}: LineSetPointProps) {
  const { openContextMenu } = useContextMenu();
  const pointUniqueId = point.uniqueId || `point-${pointIndex}`;
  const draggableId = `${linesetUniqueId}-${pointUniqueId}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: draggableId,
    data: {
      type: 'grid-lineset-point',
      pointUniqueId,
      linesetUniqueId,
      linesetPoint: point
    },
    disabled: readOnly || !draggable
  });

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<SVGCircleElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const menuItems: ContextMenuItemType[] = [];

      if (canDeletePoint && onDeletePoint) {
        menuItems.push({
          label: 'Delete Point',
          onClick: () => {
            onDeletePoint(pointUniqueId);
          }
        });
      }

      if (onDeleteLineset) {
        menuItems.push({
          label: 'Delete Line Set',
          onClick: () => {
            onDeleteLineset();
          }
        });
      }

      if (menuItems.length > 0) {
        openContextMenu(e.clientX, e.clientY, menuItems, pointUniqueId);
      }
    },
    [
      canDeletePoint,
      onDeletePoint,
      onDeleteLineset,
      pointUniqueId,
      openContextMenu
    ]
  );

  const defaultRadius = 4;
  const pointRadius = radius ?? defaultRadius;

  return (
    <circle
      ref={setNodeRef as (element: SVGCircleElement | null) => void}
      cx={previewPosition.positionX}
      cy={previewPosition.positionY}
      r={pointRadius}
      fill={transparent ? 'transparent' : '#000000'}
      stroke={transparent ? 'transparent' : '#000000'}
      strokeWidth={transparent ? 0 : 2}
      className={clsx(
        readOnly || !draggable ? 'cursor-default' : 'cursor-grab',
        isDragging && 'cursor-grabbing opacity-80'
      )}
      data-lineset-point
      {...attributes}
      {...listeners}
      onContextMenu={readOnly ? undefined : handleContextMenu}
    />
  );
}
